/**
 * Environment Configuration
 * 
 * Centralized configuration for environment variables.
 * All environment variables must be prefixed with VITE_ to be exposed to the client.
 */

interface AppConfig {
  apiUrl: string;
  keycloak: {
    url: string;
    realm: string;
    clientId: string;
  };
  app: {
    name: string;
    version: string;
    organization: string;
  };
  /** Module names (ModuleDefinition.name) left out of this build, e.g. ["ljudski-resursi"] */
  disabledModules: string[];
}

// Comma-separated list, e.g. "ljudski-resursi,korisnici". Empty or missing = all modules enabled.
const parseList = (value: string | undefined): string[] =>
  (value ?? '')
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

export const env: AppConfig = {
  // Backend exposes minimal-api endpoints at /api/{module}/{resource}
  // The base URL includes the /api prefix.
  apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:5100/api',
  keycloak: {
    url: import.meta.env.VITE_KEYCLOAK_URL || 'http://auth.tgas.local:18081',
    realm: import.meta.env.VITE_KEYCLOAK_REALM || 'tgas-erp-system',
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'tgas-erp-public-client',
  },
  app: {
    name: import.meta.env.VITE_APP_NAME || 'TGAS ERP System',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    organization: import.meta.env.VITE_APP_ORGANIZATION || 'TGAS',
  },
  disabledModules: parseList(import.meta.env.VITE_DISABLED_MODULES),
};

/** False when the module (ModuleDefinition.name) is disabled for this build */
export const isModuleEnabled = (name: string): boolean =>
  !env.disabledModules.includes(name.toLowerCase());

// Validate required environment variables
const validateEnv = () => {
  const required = [
    'VITE_API_URL',
    'VITE_KEYCLOAK_URL',
    'VITE_KEYCLOAK_REALM',
    'VITE_KEYCLOAK_CLIENT_ID',
  ];

  const missing = required.filter((key) => !import.meta.env[key]);

  if (missing.length > 0 && import.meta.env.PROD) {
    console.warn(
      `Missing environment variables: ${missing.join(', ')}. Using defaults.`
    );
  }
};

validateEnv();
